import { useLocations, useOrganisation } from '@my/directory-api/hooks';
import type { Order, OrderTask, OrderTemplatePreview, TaskTimeModeValue } from '@my/tms-api';
import {
  useCargoDefinitions,
  useCopyOrder,
  useOrderDocuments,
  useOrderTasks,
  useServiceTags,
} from '@my/tms-api/hooks';
import { parsePropertyGroups } from '@my/tms-api';
import { Loading, TextInput, cn, showToast } from '@my/ui';
import { useNavigate } from 'react-router';
import type { OrderReferenceSavePayload } from '@/utils/orderReferenceLabels';
import { AlertTriangle, Check, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { adaptTaskType } from '../lib/adapters';
import { parseCopyFunctions } from '../lib/copyFunctions';
import {
  daysBetween,
  defaultCopyOrderOptions,
  minutesOfDay,
  shiftDateTime,
  shiftIsoDate,
  todayIso,
  type CopyOrderOptions,
  type TaskTimeEdit,
} from '../lib/copyOrder';
import { formatTaskDate } from '../lib/dataCells';
import { TASK_TYPE_CONFIG, taskTypeLabel } from '../lib/taskTypeConfig';
import { QUARTER_HOUR_OPTIONS, QuarterHourTimeInput, TimeModePicker } from './inputs';
import { CargoCell } from './OrderListCells';
import { OrderStatusPill } from './OrderStatusPill';

/**
 * The right of "Start from a previous order": what would be copied from the
 * order picked on the left, and the choices that go with it — what to
 * include, the new start date, how to charge.
 *
 * <p>
 * "Use this order" sends the choices ({@link CopyOrderOptions}) with the New
 * order screen's own number, currency and typed references, and opens the new
 * order. Mount it with `key={order.id}` so picking another order starts from
 * fresh choices.
 * </p>
 */
/** What the New order screen already holds for the order being created. */
export interface NewOrderContext {
  /** Minted when the customer was picked (New order); empty until then. */
  orderNumber: string;
  /**
   * Mints the number when the copy is made instead — the Copy button on an
   * existing order, so opening and closing the dialog burns no number.
   */
  allocateOrderNumber?: () => Promise<string>;
  /** Null: the server keeps the copied order's own currency. */
  currencyIsoCode: string | null;
  /** References typed on New order — they win over copied ones. */
  references: OrderReferenceSavePayload;
}

export function CopyFromOrderPanel({
  organisationId,
  order,
  newOrder,
  onCopied,
  actionLabel = 'Use this order',
  templatePreview = null,
}: {
  organisationId: string | null;
  order: Order | null;
  newOrder: NewOrderContext;
  /**
   * Building from a customer's template rather than an order: the template as
   * an order + tasks (dates on a fixed day 0). `order` is then its order.
   */
  templatePreview?: OrderTemplatePreview | null;
  /** What happens once the copy exists. Default: say so and open it. */
  onCopied?: (created: Order) => void;
  actionLabel?: string;
}) {
  if (!order) {
    return (
      <section className="flex h-full items-center justify-center rounded-md border border-dashed border-slate-200 bg-white px-4 py-10">
        <p className="text-center text-xs italic text-slate-400">
          Pick an order on the left to see what would be copied.
        </p>
      </section>
    );
  }
  return (
    <CopyFromOrderBody
      organisationId={organisationId}
      order={order}
      newOrder={newOrder}
      onCopied={onCopied}
      actionLabel={actionLabel}
      templatePreview={templatePreview}
    />
  );
}

function CopyFromOrderBody({
  organisationId,
  order,
  newOrder,
  onCopied,
  actionLabel,
  templatePreview,
}: {
  organisationId: string | null;
  order: Order;
  newOrder: NewOrderContext;
  onCopied?: (created: Order) => void;
  actionLabel: string;
  templatePreview: OrderTemplatePreview | null;
}) {
  const template = templatePreview?.template ?? null;
  const copyMutation = useCopyOrder();
  const [allocating, setAllocating] = useState(false);
  const navigate = useNavigate();
  // A template brings its own tasks and never documents.
  const tasksQuery = useOrderTasks(template ? null : order.id);
  const documentsQuery = useOrderDocuments(template ? null : order.id);
  const tasksPending = !template && tasksQuery.isPending;
  // The red-X wording for "nothing to bring": the source is an order or a template.
  const noneHint = template ? 'None in this template' : undefined;
  const documentsPending = !template && documentsQuery.isPending;
  const cargoDefsQuery = useCargoDefinitions(organisationId);
  const serviceTagsQuery = useServiceTags(organisationId);
  const serviceTagsById = useMemo(
    () => new Map((serviceTagsQuery.data ?? []).map((t) => [t.id, t])),
    [serviceTagsQuery.data],
  );
  const locationsQuery = useLocations(organisationId);
  // Already cached — New order reads the same organisation for its own settings.
  const organisationQuery = useOrganisation(organisationId);
  const defaults = parseCopyFunctions(organisationQuery.data?.tmsCopyFunctionsJson);
  // Generally the next day: today is allowed (with a warning), the past never.
  const today = todayIso();
  const [options, setOptions] = useState<CopyOrderOptions>(() =>
    defaultCopyOrderOptions(shiftIsoDate(todayIso(), 1), defaults),
  );
  const set = <K extends keyof CopyOrderOptions>(key: K, value: CopyOrderOptions[K]) =>
    setOptions((o) => ({ ...o, [key]: value }));

  const tasks = useMemo(
    () =>
      (templatePreview ? templatePreview.tasks : (tasksQuery.data ?? []))
        .slice()
        .sort((a, b) => a.sequence - b.sequence),
    [templatePreview, tasksQuery.data],
  );
  const excluded = new Set(options.excludedTaskIds);
  // With Ferry bookings unticked there is no crossing to board, so its two
  // halves can't come across either — whatever their own tick says.
  const blockedByBookings = (task: OrderTask) => isBookingTask(task) && !options.includeBookings;
  // A location deleted since (likely on a template saved a while ago) can't be
  // copied to — the row says so rather than failing quietly.
  const locationGone = (task: OrderTask) =>
    locationsQuery.isSuccess && !locationsQuery.data.some((a) => a.id === task.addressId);
  const isIncluded = (task: OrderTask) =>
    !excluded.has(task.id) && !blockedByBookings(task) && !locationGone(task);
  const includedTasks = tasks.filter(isIncluded);

  // A booking's Boarding and Disembark are one crossing — they go or stay together.
  const toggleTask = (task: OrderTask) => {
    const ids = task.bookingId
      ? tasks.filter((t) => t.bookingId === task.bookingId).map((t) => t.id)
      : [task.id];
    setOptions((o) => {
      const next = new Set(o.excludedTaskIds);
      const leaving = !next.has(task.id);
      for (const id of ids) {
        if (leaving) next.add(id);
        else next.delete(id);
      }
      return { ...o, excludedTaskIds: [...next] };
    });
  };
  const cargoDef = cargoDefsQuery.data?.find((d) => d.id === order.cargoDefinitionId);
  // Temperature-controlled cargo: the definition has its Temperature group on.
  const temperatureControlled =
    parsePropertyGroups(cargoDef?.propertyGroupsJson).temperature !== undefined;
  // What the picked order actually has. An option it has nothing for is shown
  // as not copied (red X) rather than as a tick that would do nothing.
  const hasBookings = tasks.some(isBookingTask);
  const hasReferences = orderHasReferences(order);
  const hasDocuments = (documentsQuery.data?.length ?? 0) > 0;
  // The temperatures it ran at — the order's, then any task's own. Unit
  // numbers and seals are never copied (next time is usually another unit).
  const temperatures = [
    ...new Set(
      [order.temperatureC, ...tasks.map((t) => t.temperatureC)].filter(
        (v): v is number => v != null,
      ),
    ),
  ];
  const hasTemperature = temperatures.length > 0;
  // Anything the "Quantities & products" box would carry: goods text, counts,
  // weights, volumes, loading metres or product lines on any task.
  const hasQuantities = tasks.some((t) =>
    (t.cargoItems ?? []).some(
      (c) =>
        !!c.description?.trim() ||
        c.quantity != null ||
        c.weightKg != null ||
        c.volumeM3 != null ||
        (c.loadingMetresM ?? 0) > 0 ||
        (c.products?.length ?? 0) > 0,
    ),
  );

  // Every task keeps its distance from the first one copied: a two-day run
  // that started on a Friday still takes two days from the new start.
  const orderDate = tasks.find((t) => t.plannedDate)?.plannedDate?.slice(0, 10) ?? null;
  const firstDate =
    (includedTasks.length > 0 ? includedTasks : tasks)
      .find((t) => t.plannedDate)
      ?.plannedDate?.slice(0, 10) ?? null;
  const shiftDays =
    firstDate && options.newStartDate ? daysBetween(firstDate, options.newStartDate) : 0;

  // The start time is the FIRST copied task's: Any / AM / PM / Exact, as on
  // the task form, starting from what that task had. When it was exact and
  // stays exact, moving the time moves every exact-timed task by the same
  // amount; everything else moves by whole days.
  const firstTask = includedTasks.find((t) => t.plannedDate) ?? null;
  const firstOwnMode: TaskTimeModeValue = firstTask ? timeModeOf(firstTask) : 'Any';
  const firstOwnTime = firstTask ? exactTime(firstTask) : null;
  const startMode: TaskTimeModeValue = options.includeTimeWindows
    ? (options.newStartTimeMode ?? firstOwnMode)
    : 'Any';
  const startTime = startMode === 'Exact' ? (options.newStartTime ?? firstOwnTime ?? '') : '';
  const shiftMinutes =
    options.includeTimeWindows && firstOwnTime && startMode === 'Exact' && startTime
      ? shiftDays * 1440 + (minutesOfDay(startTime) ?? 0) - (minutesOfDay(firstOwnTime) ?? 0)
      : null;
  const timeControlsOff = !options.includeTimeWindows || !firstTask;

  // Where each task lands on the new order. Time windows copied: moved with
  // the start, keeping their spacing. Not copied: whatever was set by hand on
  // the row (its date moved by the start's shift and Any until then).
  const landingOf = (task: OrderTask): TaskTimeEdit => {
    const was = task.plannedDate?.slice(0, 10) ?? null;
    if (!options.includeTimeWindows) {
      return (
        options.taskTimes[task.id] ?? {
          date: was ? shiftIsoDate(was, shiftDays) : null,
          mode: 'Any',
          time: null,
        }
      );
    }
    const ownTime = exactTime(task);
    // A timed task moves by the exact shift (it can roll into another day);
    // an AM / PM / Any one by whole days.
    const moved =
      was && ownTime && shiftMinutes != null ? shiftDateTime(was, ownTime, shiftMinutes) : null;
    const isFirst = task.id === firstTask?.id;
    const mode = isFirst ? startMode : timeModeOf(task);
    return {
      date: moved?.date ?? (was ? shiftIsoDate(was, shiftDays) : null),
      mode,
      time: mode === 'Exact' ? (isFirst ? startTime || null : (moved?.time ?? ownTime)) : null,
    };
  };

  // Never in the past; today is allowed but flagged.
  const nowMinutes = (() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  })();
  const whenOf = (landing: TaskTimeEdit) => timingOf(landing, today, nowMinutes);
  const startTiming = whenOf({
    date: options.newStartDate || null,
    mode: startMode,
    time: startTime || null,
  });
  const taskTimings = includedTasks.map((t) => whenOf(landingOf(t)));
  const anyPast = startTiming === 'past' || taskTimings.includes('past');
  const anyToday = !anyPast && (startTiming === 'today' || taskTimings.includes('today'));
  // An Exact start needs its time before anything can be built from it.
  const missingStartTime = startMode === 'Exact' && !startTime && !timeControlsOff;

  const blockedReason = anyPast
    ? 'The new order can’t start in the past.'
    : missingStartTime
      ? 'Pick the start time.'
      : !newOrder.orderNumber && !newOrder.allocateOrderNumber
        ? 'Waiting for the new order’s number.'
        : tasksPending
          ? 'Loading the tasks…'
          : null;

  const createCopy = async () => {
    if (blockedReason || !organisationId) return;
    let orderNumber = newOrder.orderNumber;
    if (!orderNumber && newOrder.allocateOrderNumber) {
      setAllocating(true);
      try {
        orderNumber = await newOrder.allocateOrderNumber();
      } catch (err) {
        showToast({
          type: 'error',
          message: err instanceof Error ? err.message : 'Couldn’t get a number for the new order.',
        });
        return;
      } finally {
        setAllocating(false);
      }
    }
    copyMutation.mutate(
      {
        sourceOrderId: template ? null : order.id,
        sourceTemplateId: template?.id ?? null,
        organisationId,
        orderNumber,
        currencyIsoCode: newOrder.currencyIsoCode,
        ...newOrder.references,
        // What the picked order has nothing for is off, whatever the tick says.
        includeQuantities: hasQuantities && options.includeQuantities,
        includeTimeWindows: options.includeTimeWindows,
        includeTaskNotes: options.includeTaskNotes,
        includeBookings: hasBookings && options.includeBookings,
        includeReferences: hasReferences && options.includeReferences,
        includeDocuments: hasDocuments && options.includeDocuments,
        includeTemperature: temperatureControlled && hasTemperature && options.includeTemperature,
        status: options.status,
        charges: options.charges,
        tasks: includedTasks.map((t) => {
          const landing = landingOf(t);
          return {
            sourceTaskId: t.id,
            plannedDate: landing.date,
            timeMode: landing.mode,
            plannedTime: landing.mode === 'Exact' && landing.time ? `${landing.time}:00` : null,
          };
        }),
      },
      {
        onSuccess: (created) => {
          if (onCopied) {
            onCopied(created);
            return;
          }
          showToast({
            type: 'success',
            message: template
              ? `Order ${created.orderNumber} created from template “${template.name}”.`
              : `Order ${created.orderNumber} created from ${order.orderNumber}.`,
          });
          navigate(`/orders/${created.id}`);
        },
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : 'Couldn’t copy the order.',
          }),
      },
    );
  };
  const locationName = (id: string) =>
    locationsQuery.data?.find((a) => a.id === id)?.name ?? 'Unknown location';

  return (
    <section className="flex h-full flex-col gap-3 rounded-md border border-slate-200 bg-white p-3">
      {/* What it's copying, and the cargo that always comes with it — a copy
          repeats a job, so the cargo, its service tags and the tasks are not
          options. */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {template ? 'From template' : 'Copying from'}
          </p>
          {template ? (
            <>
              <p className="mt-0.5 text-sm font-semibold text-slate-900">{template.name}</p>
              {template.description && (
                <p className="mt-0.5 text-xs text-slate-500">{template.description}</p>
              )}
            </>
          ) : (
            <>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{order.orderNumber}</span>
                <OrderStatusPill status={order.status} />
              </div>
              {orderDate && (
                <p className="mt-0.5 text-xs text-slate-500">{formatTaskDate(orderDate)}</p>
              )}
            </>
          )}
        </div>
        <div>
          <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
            Cargo
          </p>
          <CargoCell order={order} cargoDef={cargoDef} serviceTagsById={serviceTagsById} />
        </div>
      </div>

      {/* What comes across — the user's call, starting from the organisation's
          defaults. Leaving a single task out is done on its row below. */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {hasQuantities ? (
          <Option
            label="Quantities & products"
            checked={options.includeQuantities}
            onChange={(v) => set('includeQuantities', v)}
          />
        ) : (
          <Unavailable label="Quantities & products" hint={noneHint} />
        )}
        <Option
          label="Time windows"
          checked={options.includeTimeWindows}
          onChange={(v) => set('includeTimeWindows', v)}
        />
        <Option
          label="Task notes"
          checked={options.includeTaskNotes}
          onChange={(v) => set('includeTaskNotes', v)}
        />
        {hasBookings ? (
          <Option
            label="Ferry bookings"
            hint="Carrier and route; the sailing is re-booked"
            checked={options.includeBookings}
            onChange={(v) => set('includeBookings', v)}
          />
        ) : (
          <Unavailable label="Ferry bookings" hint={noneHint} />
        )}
        {temperatureControlled &&
          (hasTemperature ? (
            <Option
              label="Temperature"
              hint={temperatures.map((c) => `${c}°C`).join(', ')}
              checked={options.includeTemperature}
              onChange={(v) => set('includeTemperature', v)}
            />
          ) : (
            <Unavailable label="Temperature" hint={noneHint} />
          ))}
        {hasReferences ? (
          <Option
            label="References"
            checked={options.includeReferences}
            onChange={(v) => set('includeReferences', v)}
          />
        ) : (
          <Unavailable label="References" hint={noneHint} />
        )}
        {template ? (
          <Unavailable label="Documents" hint="Not kept in templates" />
        ) : documentsPending ? (
          <Option
            label="Documents"
            checked={options.includeDocuments}
            disabled
            onChange={() => {}}
          />
        ) : hasDocuments ? (
          <Option
            label="Documents"
            checked={options.includeDocuments}
            onChange={(v) => set('includeDocuments', v)}
          />
        ) : (
          <Unavailable label="Documents" />
        )}
        {/* Set by support for the organisation, not per copy. */}
        <Always label={`Starts as ${options.status}`} />
      </div>

      {/* Charges — title above, the switch with what it means beside it. */}
      <div>
        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Charges
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="grid w-56 shrink-0 grid-cols-2 rounded-md bg-slate-100 p-0.5 text-xs">
            {(
              [
                ['Reprice', 'Re-price'],
                ['CopyPrevious', template ? 'Template prices' : 'Copy previous'],
              ] as const
            ).map(([value, label]) => {
              // A template saved without charge lines has no prices to bring.
              const off = value === 'CopyPrevious' && !!template && !template.hasCharges;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={off}
                  title={off ? 'This template was saved without charges' : undefined}
                  aria-pressed={options.charges === value}
                  onClick={() => set('charges', value)}
                  className={cn(
                    'rounded-sm px-2 py-1 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                    options.charges === value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-400">
            {options.charges === 'Reprice'
              ? 'Priced from the customer’s rate card.'
              : template
                ? 'The lines and amounts saved with the template.'
                : 'The same lines and amounts as this order.'}
          </p>
        </div>
      </div>

      {/* New start — the first copied task's date and time, laid out like the
          task form: date, the time when Exact, and the time mode. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-40 shrink-0">
          <TextInput
            label="New start date"
            type="date"
            min={today}
            value={options.newStartDate}
            error={startTiming === 'past'}
            onChange={(e) => set('newStartDate', e.target.value)}
          />
        </div>
        {startMode === 'Exact' && !timeControlsOff && (
          <div className="w-28 shrink-0">
            <QuarterHourTimeInput
              label="Start time"
              value={startTime}
              onChange={(v) => set('newStartTime', v || null)}
            />
          </div>
        )}
        <div
          className={cn(
            'flex min-h-9 items-center gap-1.5',
            timeControlsOff && 'pointer-events-none opacity-50',
          )}
          title={
            !options.includeTimeWindows
              ? 'Time windows aren’t being copied — every task is Any'
              : undefined
          }
        >
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            Time mode
          </span>
          <TimeModePicker
            value={startMode === 'Any' ? 'Undefined' : startMode}
            onChange={(m) => set('newStartTimeMode', m === 'Undefined' ? 'Any' : m)}
          />
        </div>
        {!options.includeTimeWindows && (
          <p className="text-[10px] text-slate-400">Set each task’s date and time below.</p>
        )}
      </div>

      {/* The tasks as they'd land on the new order */}
      <div className="min-h-0">
        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
          Tasks on the new order
          {tasks.length > 0 && includedTasks.length < tasks.length && (
            <span className="ml-1 normal-case tracking-normal text-slate-400">
              ({includedTasks.length} of {tasks.length})
            </span>
          )}
        </p>
        {tasksPending ? (
          <Loading label="Loading tasks" />
        ) : tasks.length === 0 ? (
          <p className="text-xs italic text-slate-400">This order has no tasks.</p>
        ) : (
          <ol className="max-h-56 divide-y divide-slate-100 overflow-auto rounded-sm border border-slate-100">
            {tasks.map((task) => {
              const included = isIncluded(task);
              return (
                <NewTaskRow
                  key={task.id}
                  // Numbered as they'll land: an unticked task leaves no gap.
                  index={included ? includedTasks.indexOf(task) + 1 : null}
                  task={task}
                  included={included}
                  lockedReason={
                    blockedByBookings(task)
                      ? 'Ferry bookings aren’t being copied'
                      : locationGone(task)
                        ? 'This location no longer exists'
                        : null
                  }
                  fromTemplate={!!template}
                  onToggle={() => toggleTask(task)}
                  locationName={locationName(task.addressId)}
                  landing={landingOf(task)}
                  timing={included ? whenOf(landingOf(task)) : null}
                  today={today}
                  // Times not copied: each task's date and time are the user's.
                  editable={!options.includeTimeWindows}
                  onEdit={(next) =>
                    setOptions((o) => ({ ...o, taskTimes: { ...o.taskTimes, [task.id]: next } }))
                  }
                />
              );
            })}
          </ol>
        )}
      </div>

      {anyPast ? (
        <p className="flex items-start gap-1.5 rounded-sm bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <X className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          The new order can’t start in the past — move the start date, or the tasks marked in red.
        </p>
      ) : anyToday ? (
        <p className="flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Starts today — check the work can still be done in time.
        </p>
      ) : null}

      <button
        type="button"
        disabled={blockedReason != null || copyMutation.isPending || allocating}
        title={blockedReason ?? undefined}
        onClick={() => void createCopy()}
        className="mt-auto w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {copyMutation.isPending || allocating ? 'Copying…' : actionLabel}
      </button>
    </section>
  );
}

function NewTaskRow({
  index,
  task,
  included,
  lockedReason,
  onToggle,
  locationName,
  landing,
  timing,
  today,
  editable,
  onEdit,
  fromTemplate,
}: {
  /** Position on the new order; null when the task is left off. */
  index: number | null;
  task: OrderTask;
  included: boolean;
  /** Why the task can't be ticked right now (e.g. a booking half with bookings off). */
  lockedReason: string | null;
  onToggle: () => void;
  locationName: string;
  /** Where the task lands on the new order — worked out by the panel. */
  landing: TaskTimeEdit;
  /** Past (not allowed) / today (allowed, flagged); null for later or not copied. */
  timing: Timing;
  today: string;
  /** The date and time are set by hand on the row (time windows not copied). */
  editable: boolean;
  onEdit: (next: TaskTimeEdit) => void;
  /** A template task: its "date" is a day of the job ("Day 2"), not a past date. */
  fromTemplate: boolean;
}) {
  const cfg = TASK_TYPE_CONFIG[adaptTaskType(task.type)];
  const Icon = cfg.icon;
  const was = task.plannedDate?.slice(0, 10) ?? null;
  const ownTime = exactTime(task);
  const becomes = landing.date;
  const time = timeLabel(landing.mode, landing.time);
  const ownLabel = timeLabel(timeModeOf(task), ownTime);
  const wasTime = time !== ownLabel ? ` · ${ownLabel}` : '';
  const editing = included && editable;
  const wasDay = was
    ? fromTemplate
      ? `Day ${daysBetween(TEMPLATE_DAY_ZERO, was) + 1}`
      : `was ${formatTaskDate(was)}`
    : null;
  return (
    <li className={cn('px-2 py-1.5', !included && 'bg-slate-50')}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={included}
          disabled={lockedReason != null}
          onChange={onToggle}
          aria-label={`Copy ${taskTypeLabel(task.type)} at ${locationName}`}
          title={
            lockedReason ?? (task.bookingId ? 'Boarding and disembark go together' : undefined)
          }
          className="h-3.5 w-3.5 shrink-0 disabled:cursor-not-allowed"
        />
        <span className="w-4 shrink-0 text-right text-[11px] text-slate-400">{index ?? ''}</span>
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
            cfg.tone,
            !included && 'opacity-50',
          )}
        >
          <Icon className="h-3 w-3" aria-hidden="true" />
          {taskTypeLabel(task.type)}
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs',
            included ? 'text-slate-800' : 'text-slate-400 line-through',
          )}
          title={locationName}
        >
          {locationName}
        </span>
        <span className="shrink-0 text-right">
          {!included ? (
            <span className="text-[11px] italic text-slate-400" title={lockedReason ?? undefined}>
              Not copied
            </span>
          ) : editing ? (
            // Set by hand: the pickers on the right, what it was underneath.
            <>
              <TaskTimeEditor
                value={landing}
                onChange={onEdit}
                label={locationName}
                today={today}
                timing={timing}
              />
              {wasDay && (
                <span className="mt-0.5 block text-[10px] text-slate-400">
                  {wasDay} · {ownLabel}
                </span>
              )}
            </>
          ) : becomes ? (
            <>
              <span
                className={cn(
                  'block text-[11px] font-semibold',
                  timing === 'past'
                    ? 'text-red-600'
                    : timing === 'today'
                      ? 'text-amber-700'
                      : 'text-blue-700',
                )}
                title={timing === 'past' ? 'In the past' : timing === 'today' ? 'Today' : undefined}
              >
                {formatTaskDate(becomes)} · {time}
              </span>
              {(fromTemplate || becomes !== was || wasTime) && wasDay && (
                <span className="block text-[10px] text-slate-400">
                  {wasDay}
                  {fromTemplate ? ` · ${ownLabel}` : wasTime}
                </span>
              )}
            </>
          ) : (
            <span className="text-[11px] italic text-slate-300">No date</span>
          )}
        </span>
      </div>
    </li>
  );
}

/**
 * One task's date and time, set by hand — compact, at the right of its row:
 * date, the time mode (Any / AM / PM / Exact, as on the task form) and the
 * quarter-hour time when Exact.
 */
function TaskTimeEditor({
  value,
  onChange,
  label,
  today,
  timing,
}: {
  value: TaskTimeEdit;
  onChange: (next: TaskTimeEdit) => void;
  label: string;
  today: string;
  timing: Timing;
}) {
  const ring =
    timing === 'past'
      ? 'border-red-500 focus:ring-red-500'
      : timing === 'today'
        ? 'border-amber-400 focus:ring-amber-400'
        : 'border-slate-300 focus:ring-blue-500';
  const modes: { mode: TaskTimeModeValue; text: string }[] = [
    { mode: 'Any', text: 'Any' },
    { mode: 'AM', text: 'AM' },
    { mode: 'PM', text: 'PM' },
    { mode: 'Exact', text: 'Exact' },
  ];
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <input
        type="date"
        aria-label={`Date at ${label}`}
        min={today}
        value={value.date ?? ''}
        onChange={(e) => onChange({ ...value, date: e.target.value || null })}
        className={cn(
          'h-7 rounded-sm border bg-white px-1.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2',
          ring,
        )}
      />
      <div className="flex items-center gap-1">
        {modes.map(({ mode, text }) => (
          <button
            key={mode}
            type="button"
            onClick={() =>
              onChange({ ...value, mode, time: mode === 'Exact' ? (value.time ?? '09:00') : null })
            }
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors',
              value.mode === mode
                ? 'border-purple-300 bg-purple-50 text-purple-700'
                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {text}
          </button>
        ))}
      </div>
      {value.mode === 'Exact' && (
        <select
          aria-label={`Time at ${label}`}
          value={value.time ?? ''}
          onChange={(e) => onChange({ ...value, time: e.target.value || null })}
          className="h-7 rounded-sm border border-slate-300 bg-white px-1 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {QUARTER_HOUR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** When a task lands, against now: in the past, today, or null (later / no date). */
type Timing = 'past' | 'today' | null;

/**
 * Past is never allowed, today is allowed but flagged. Today counts as past
 * once the window has gone: an exact time already behind us, or AM after midday.
 */
function timingOf(landing: TaskTimeEdit, today: string, nowMinutes: number): Timing {
  if (!landing.date) return null;
  if (landing.date < today) return 'past';
  if (landing.date > today) return null;
  if (landing.mode === 'Exact' && landing.time) {
    return (minutesOfDay(landing.time) ?? 0) < nowMinutes ? 'past' : 'today';
  }
  if (landing.mode === 'AM' && nowMinutes >= 12 * 60) return 'past';
  return 'today';
}

/** "AM" / "PM" / "17:30" / "Any". */
function timeLabel(mode: TaskTimeModeValue, time: string | null): string {
  if (mode === 'AM') return 'AM';
  if (mode === 'PM') return 'PM';
  if (mode === 'Exact' && time) return time;
  return 'Any';
}

/** Where a template's day 0 lands when the server turns it into an order to read (OrderTemplateBody.DayZero). */
const TEMPLATE_DAY_ZERO = '2000-01-03';

/** A booking's Boarding or Disembark half. */
function isBookingTask(task: OrderTask): boolean {
  return !!task.bookingId || task.type === 'BookingDeparture' || task.type === 'BookingArrival';
}

/** The task's time mode, reading an old row with a time but no mode as Exact. */
function timeModeOf(task: OrderTask): TaskTimeModeValue {
  if (task.timeMode) return task.timeMode;
  return task.plannedTime ? 'Exact' : 'Any';
}

/** The task's exact `HH:mm`, or null for an AM / PM / Any window (or no time). */
function exactTime(task: OrderTask): string | null {
  if (task.timeMode === 'AM' || task.timeMode === 'PM' || task.timeMode === 'Any') return null;
  return task.plannedTime ? task.plannedTime.slice(0, 5) : null;
}

/** Any reference slot filled — the four indexed ones or the long tail. */
function orderHasReferences(order: Order): boolean {
  if (
    [order.reference1, order.reference2, order.reference3, order.reference4].some((r) => r?.trim())
  )
    return true;
  if (!order.additionalReferencesJson) return false;
  try {
    const extra = JSON.parse(order.additionalReferencesJson) as unknown;
    return (
      !!extra &&
      typeof extra === 'object' &&
      Object.values(extra as Record<string, unknown>).some((v) => typeof v === 'string' && v.trim())
    );
  } catch {
    return false;
  }
}

function Always({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-700">
      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
      {label}
    </div>
  );
}

/** Something that isn't copied — the picked order has none of it. */
function Unavailable({ label, hint = 'None on this order' }: { label: string; hint?: string }) {
  return (
    <div className="flex items-start gap-1.5 text-xs text-slate-400" title={hint}>
      <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" aria-hidden="true" />
      <span>
        {label}
        <span className="block text-[10px]">{hint}</span>
      </span>
    </div>
  );
}

function Option({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  /** Greyed out — e.g. task notes when no tasks are copied. Keeps its tick for when it comes back. */
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-1.5 text-xs',
        disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700',
      )}
      title={hint}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 shrink-0"
      />
      <span>
        {label}
        {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}
